import { useState, useEffect } from 'react';
import { builderApi } from '../api';
import { fmtDate } from '../utils';

const PROJ_STATUS_COLOR = { ACTIVE: '#38a169', IN_PROGRESS: '#805ad5', COMPLETED: '#3182ce', ON_HOLD: '#d69e2e', CANCELLED: '#e53e3e' };
const WORK_STATUS_COLOR = { PENDING: '#718096', IN_PROGRESS: '#3182ce', COMPLETED: '#38a169', ON_HOLD: '#d69e2e' };
const WORK_STATUS_ORDER = { IN_PROGRESS: 0, PENDING: 1, ON_HOLD: 2, COMPLETED: 3 };

const linkStyle = {
  cursor: 'pointer',
  transition: 'box-shadow .15s, transform .12s',
};

function NavCard({ color, icon, value, label, sub, to, navigate }) {
  return (
    <div
      className="bld-card"
      onClick={() => navigate(to)}
      style={{
        borderLeft: `4px solid ${color}`, margin: 0,
        cursor: 'pointer',
        transition: 'box-shadow .15s, transform .12s',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 4px 16px ${color}33`; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.transform = ''; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 28 }}>{icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginTop: 2 }}>{label}</div>
          <div style={{ fontSize: 11, color: '#718096', marginTop: 2 }}>{sub}</div>
        </div>
        <div style={{ fontSize: 16, color: color, opacity: 0.5 }}>›</div>
      </div>
    </div>
  );
}

export default function BuilderDashboard({ navigate = () => {} }) {
  const [projects, setProjects]   = useState([]);
  const [inventory, setInventory] = useState([]);
  const [work, setWork]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [err, setErr]             = useState('');

  useEffect(() => {
    Promise.all([
      builderApi.getProjects(),
      builderApi.getInventory(),
      builderApi.getWork(),
    ]).then(([p, inv, w]) => {
      setProjects(p || []);
      setInventory(inv || []);
      setWork(w || []);
    }).catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: '#718096', fontSize: 14, padding: 24 }}>Loading dashboard…</div>;

  // ── Derived metrics ──────────────────────────────────────────────────────────
  const ongoing   = projects.filter(p => p.status === 'ACTIVE' || p.status === 'IN_PROGRESS').length;
  const completed = projects.filter(p => p.status === 'COMPLETED').length;
  const lowStock  = inventory.filter(i => Number(i.quantity) < 10).length;

  const inProgressWork = work.filter(w => w.status === 'IN_PROGRESS');
  const pendingWork    = work.filter(w => w.status === 'PENDING');
  const totalLabour    = work.reduce((s, w) => s + Number(w.labour_charge || 0), 0);

  const sortedWork = [...work].sort((a, b) => {
    const so = (WORK_STATUS_ORDER[a.status] ?? 9) - (WORK_STATUS_ORDER[b.status] ?? 9);
    if (so !== 0) return so;
    return (b.work_date || '').localeCompare(a.work_date || '');
  });
  const recentWork = sortedWork.slice(0, 6);

  const projectWorkMap = work.reduce((acc, w) => {
    const pid = String(w.project_id);
    if (!acc[pid]) acc[pid] = { entries: [], inProgress: 0, pending: 0, labour: 0, maxPct: 0 };
    acc[pid].entries.push(w);
    if (w.status === 'IN_PROGRESS') acc[pid].inProgress++;
    if (w.status === 'PENDING')     acc[pid].pending++;
    acc[pid].labour  += Number(w.labour_charge || 0);
    acc[pid].maxPct   = Math.max(acc[pid].maxPct, Number(w.completion_pct || 0));
    return acc;
  }, {});

  return (
    <div>
      {err && (
        <div style={{ background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 8,
          padding: '10px 16px', marginBottom: 16, color: '#c53030', fontSize: 13 }}>
          ⚠ Could not load some data: {err}
        </div>
      )}

      {/* ── Stat cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <NavCard color="#3182ce" icon="🏗️" value={ongoing}
          label="Ongoing Projects" sub={`${completed} completed`}
          to="projects" navigate={navigate} />
        <NavCard color="#805ad5" icon="👷" value={inProgressWork.length}
          label="Work In Progress" sub={`${pendingWork.length} pending`}
          to="work" navigate={navigate} />
        <NavCard color="#38a169" icon="📦" value={inventory.length}
          label="Material Types" sub={lowStock > 0 ? `⚠ ${lowStock} low stock` : 'Stock OK'}
          to="inventory" navigate={navigate} />
        <NavCard color="#d69e2e" icon="💰" value={`₹${totalLabour.toLocaleString('en-IN')}`}
          label="Total Labour Cost" sub={`across ${work.length} work entries`}
          to="payments" navigate={navigate} />
      </div>

      {/* ── Project Progress + Recent Work ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>

        {/* Project Progress */}
        <div className="bld-card" style={{ margin: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
              Project Progress
            </h3>
            <button onClick={() => navigate('projects')}
              style={{ background: 'none', border: '1px solid var(--border-color)', borderRadius: 6,
                padding: '3px 10px', fontSize: 11, color: '#3182ce', cursor: 'pointer', fontWeight: 600 }}>
              View All →
            </button>
          </div>
          {projects.length === 0 ? (
            <div style={{ color: '#718096', fontSize: 13 }}>No projects yet —{' '}
              <span style={{ color: '#3182ce', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate('projects')}>Add one</span>
            </div>
          ) : projects.slice(0, 6).map(p => {
            const pw = projectWorkMap[String(p.id)];
            const pct = pw ? pw.maxPct : 0;
            const wip = pw ? pw.inProgress : 0;
            return (
              <div key={p.id} style={{ marginBottom: 14, cursor: 'pointer', borderRadius: 6, padding: '4px 6px', margin: '0 -6px 10px' }}
                onClick={() => navigate('projects')}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{p.name}</span>
                    {wip > 0 && (
                      <span style={{ marginLeft: 6, fontSize: 10, background: '#ebf8ff', color: '#2b6cb0',
                        borderRadius: 8, padding: '1px 6px', fontWeight: 600 }}>
                        {wip} active
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: PROJ_STATUS_COLOR[p.status] || '#718096' }}>
                      {p.status?.replace('_', ' ')}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#4a5568' }}>{pct}%</span>
                  </div>
                </div>
                <div style={{ height: 7, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{
                    width: `${pct}%`, height: '100%', borderRadius: 4,
                    background: pct === 100 ? '#38a169' : p.status === 'IN_PROGRESS' ? '#805ad5' : '#3182ce',
                    transition: 'width .4s',
                  }} />
                </div>
                {pw && (
                  <div style={{ fontSize: 10, color: '#718096', marginTop: 3 }}>
                    {pw.entries.length} work {pw.entries.length === 1 ? 'entry' : 'entries'}
                    {pw.labour > 0 && ` · ₹${pw.labour.toLocaleString('en-IN')} labour`}
                  </div>
                )}
              </div>
            );
          })}
          {projects.length > 6 && (
            <div style={{ textAlign: 'center', marginTop: 4 }}>
              <span style={{ fontSize: 11, color: '#3182ce', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate('projects')}>
                +{projects.length - 6} more projects
              </span>
            </div>
          )}
        </div>

        {/* Work Entries */}
        <div className="bld-card" style={{ margin: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
              Work Entries
              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: '#718096' }}>
                (in-progress first)
              </span>
            </h3>
            <button onClick={() => navigate('work')}
              style={{ background: 'none', border: '1px solid var(--border-color)', borderRadius: 6,
                padding: '3px 10px', fontSize: 11, color: '#805ad5', cursor: 'pointer', fontWeight: 600 }}>
              View All →
            </button>
          </div>
          {recentWork.length === 0 ? (
            <div style={{ color: '#718096', fontSize: 13 }}>No work logged yet —{' '}
              <span style={{ color: '#805ad5', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate('work')}>Log work</span>
            </div>
          ) : recentWork.map(w => (
            <div key={w.id}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                padding: '8px 6px', borderBottom: '1px solid var(--border)', cursor: 'pointer', borderRadius: 6, margin: '0 -6px' }}
              onClick={() => navigate('work')}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {w.trade} — {w.project_name}
                </div>
                <div style={{ fontSize: 11, color: '#718096', marginTop: 1 }}>
                  {w.worker_name || 'No worker'} · {fmtDate(w.work_date)}
                </div>
                <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 60, height: 5, background: '#e2e8f0', borderRadius: 3 }}>
                    <div style={{ width: `${w.completion_pct || 0}%`, height: '100%',
                      background: WORK_STATUS_COLOR[w.status] || '#718096', borderRadius: 3 }} />
                  </div>
                  <span style={{ fontSize: 10, color: '#4a5568' }}>{w.completion_pct || 0}%</span>
                </div>
              </div>
              <div style={{ marginLeft: 10, flexShrink: 0, textAlign: 'right' }}>
                <span style={{ background: WORK_STATUS_COLOR[w.status] || '#718096', color: '#fff',
                  padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                  display: 'inline-block', whiteSpace: 'nowrap' }}>
                  {w.status?.replace('_', ' ')}
                </span>
                {w.labour_charge > 0 && (
                  <div style={{ fontSize: 10, color: '#718096', marginTop: 3 }}>
                    ₹{Number(w.labour_charge).toLocaleString('en-IN')}
                  </div>
                )}
              </div>
            </div>
          ))}
          {work.length > 6 && (
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <span style={{ fontSize: 11, color: '#805ad5', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate('work')}>
                +{work.length - 6} more entries
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Low stock alert ── */}
      {lowStock > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #f6e05e', borderRadius: 8, padding: '12px 16px',
          cursor: 'pointer' }}
          onClick={() => navigate('inventory')}
          onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
          onMouseLeave={e => e.currentTarget.style.background = '#fffbeb'}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontWeight: 700, color: '#744210', fontSize: 13 }}>⚠ Low Stock Alert</div>
            <span style={{ fontSize: 11, color: '#975a16', fontWeight: 600 }}>View Inventory →</span>
          </div>
          <div style={{ color: '#975a16', fontSize: 12, marginTop: 4 }}>
            {inventory.filter(i => Number(i.quantity) < 10 && Number(i.quantity) >= 0).map(i =>
              `${i.material_name} (${Number(i.quantity).toFixed(2)} ${i.unit})`
            ).join(' · ')}
          </div>
        </div>
      )}

    </div>
  );
}
